import "twin.macro";

import { browserMightBeOniOS, madeWithGlide, TailwindThemeProvider } from "@glide/common";
import { getSpinner } from "@glide/common-core/dist/js/components/activity-spinner/activity-spinner";
import classNames from "classnames";
import { useEffect, useState } from "react";
import type { BaseProps } from "./custom-sign-in-props";
import { CustomSignInStyle } from "./custom-sign-in-style";
import EmailInput from "./email-input";
import { canShowExternalAuthButtons } from "./google-sso";
import NameInput from "./name-input";
import PasswordInput from "./password-input";
import { PlayerContentPlaceholder } from "@glide/common-components";
import { useWireAppTheme } from "../../utils/use-wireapp-theme";
import { AppKind } from "@glide/location-common";

const MadeWithGlideIcon = madeWithGlide;

export const CustomSignIn: React.FC<React.PropsWithChildren<BaseProps>> = p => {
    const [visualViewportHeight, setVisualViewportHeight] = useState<number | undefined>(undefined);
    const theme = useWireAppTheme();

    useEffect(() => {
        if (window.visualViewport !== undefined && browserMightBeOniOS) {
            (window.visualViewport as any).addEventListener("scroll", viewportHandler);
            (window.visualViewport as any).addEventListener("resize", viewportHandler);
        }
        return () => {
            if (window.visualViewport !== undefined && browserMightBeOniOS) {
                (window.visualViewport as any).removeEventListener("scroll", viewportHandler);
                (window.visualViewport as any).removeEventListener("resize", viewportHandler);
            }
        };
    }, []);

    const viewportHandler = (e: any) => {
        setVisualViewportHeight(e.target.height);
    };

    const {
        loginType,
        colorTheme,
        currentState,
        background,
        backgroundOverlay,
        isPro,
        useParallax,
        appFeatures,
        previewMode,
    } = p;
    let buttonOffset = 0;

    if (visualViewportHeight !== undefined && window.innerHeight !== visualViewportHeight && window.innerHeight > 500) {
        buttonOffset = window.innerHeight - visualViewportHeight;
        // FIXME: This is a hack to make the virtual keyboard overlap the Sign in with Google
        // button. This only works when there's one external auth button, too.
        // Either make this method report the number of auth buttons we can show or
        // figure out a better way to handle keyboard overlap.
        if (canShowExternalAuthButtons(appFeatures)) {
            buttonOffset -= 66;
        }
    }

    if (currentState === "sign-in") {
        const showBackgroundImage =
            appFeatures?.useCustomSignInBackgroundImageInLoading === true && background !== undefined;
        return (
            <TailwindThemeProvider theme={theme}>
                <PlayerContentPlaceholder
                    data-test="app-signing-in"
                    backgroundImage={showBackgroundImage ? background : undefined}>
                    {!isPro && <MadeWithGlideIcon fgColor={theme.fgColorDark} />}
                    <div className="spinner">{getSpinner(theme.primaryAccentColor, "loading-placeholder")}</div>
                </PlayerContentPlaceholder>
            </TailwindThemeProvider>
        );
    }

    // there was a bug where "none" got put in as "no" so treat them the same
    return (
        <CustomSignInStyle
            className={classNames(
                `${
                    backgroundOverlay === undefined ||
                    backgroundOverlay === "none" ||
                    backgroundOverlay === ("no" as any) ||
                    background === undefined
                        ? "accent"
                        : "black"
                }`,
                AppKind.Page
            )}
            colorTheme={colorTheme}
            useParallax={useParallax === true && currentState === "challenge"}
            isBuilder={true}
            buttonOffset={buttonOffset}
            previewMode={previewMode}
            role="dialog"
            aria-modal={true}
            aria-labelledby="sign-in-description">
            <div className="bc-main-content">
                <div className="bc-content-container">
                    {loginType === "none" && currentState !== "request-access-complete" && <NameInput {...p} />}
                    {loginType === "password" && <PasswordInput theme={theme} {...p} />}
                    {loginType === "email" && <EmailInput theme={theme} {...p} />}
                </div>
            </div>
        </CustomSignInStyle>
    );
};
